import { useMemo, useState } from "react";
import useFetchPaginated from "#src/hooks/useFetchPaginated";
import paymentService from "#src/services/payment";
import { useOutletContext } from "react-router-dom";
import { useFilter } from "#src/commonV2/Table/Filter/useFilter";
import { useTranslation } from "react-i18next";
import { SearchboxV2 } from "#src/commonV2/Table/Searchbox";
import { TableV2 } from "#src/commonV2/Table";
import { Tooltip } from "@lockerpm/design";
import { expandPlanDuration } from "#src/utils/apiHelper";
import { DateTime } from "luxon";
import { PaymentStatusValue } from "#src/config/filter/workspace/value";
import {
  CheckCircle,
  Clock,
  CreditCard02,
  Repeat02,
} from "@untitled-ui/icons-react";
import constants from "#src/config/constants";
import { IPaymentItem } from "#src/services/payment/types";
import { PaymentMethodValue } from "#src/services/payment/enum";
import { CustomBadgeV2ForStatus } from "#src/components/workspace/billing/CustomBadge";
import { paymentStatusItems } from "#src/components/workspace/billing/order/filterItems";
import { paymentMethodItems } from "#src/components/workspace/billing/solution/filterItems";
import { FilterDropdownV2 } from "#src/commonV2/Table/Filter/FilterDropdown";

export enum BillingOrderSearchParamKey {
  STATUS = "status",
  PAYMENT_METHOD = "payment_method",
}

export const BillingOrderTab = () => {
  const { workspaceId } = useOutletContext<{
    workspaceId: string;
  }>();

  const { t, i18n } = useTranslation("workspace", {
    keyPrefix: "billing.tab.order",
  });

  const locale = i18n.language;

  // --------------- STATES ---------------

  // Pagination
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(constants.DEFAULT_PAGE_SIZE);

  // Search/sort
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  const { value: paymentStatus, renderProps: paymentStatusFilterRenderProps } =
    useFilter({
      label: t("filter.paymentStatus.label"),
      selectionList: paymentStatusItems,
      options: {
        searchParamKey: BillingOrderSearchParamKey.STATUS,
      },
    });

  const { value: paymentMethod, renderProps: paymentMethodFilterRenderProps } =
    useFilter({
      label: t("filter.paymentMethod.label"),
      selectionList: paymentMethodItems,
      options: {
        searchParamKey: BillingOrderSearchParamKey.PAYMENT_METHOD,
      },
    });

  // --------------- COMPUTED ---------------

  const fetchParams = useMemo<Parameters<typeof paymentService.listPayments>>(
    () => [
      workspaceId,
      {
        status: paymentStatus,
        payment_method: paymentMethod,
        page: selectedPage,
        size: pageSize,
        q: searchKeyword,
      },
    ],
    [
      workspaceId,
      paymentStatus,
      paymentMethod,
      selectedPage,
      pageSize,
      searchKeyword,
    ]
  );

  // --------------- METHODS ---------------

  const onChangePage = (page: number) => {
    setSelectedPage(page);
  };

  const onChangePageSize = (size: number) => {
    setPageSize(size);
    setSelectedPage(1);
  };

  // --------------- HOOKS ---------------

  const {
    list: orderList,
    count: orderCount,
    isLoading,
  } = useFetchPaginated(paymentService.listPayments, fetchParams);

  // --------------- RENDER ---------------

  return (
    <>
      <div className="flex justify-between items-start gap-2">
        <div className="flex gap-4 justify-between flex-1">
          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            <FilterDropdownV2 {...paymentStatusFilterRenderProps} />
            <FilterDropdownV2 {...paymentMethodFilterRenderProps} />
          </div>
          {/* Filters end */}
        </div>

        {/* Searchbox */}
        <SearchboxV2
          value={searchKeyword}
          onChange={(value) => {
            setSearchKeyword(value);
          }}
          placeholder={t("filter.searchbox.placeholder")}
        />
        {/* Searchbox end */}
      </div>

      <TableV2<IPaymentItem>
        data={orderList ?? []}
        getRowKey={(item) => item.payment_id}
        isLoading={isLoading}
        columns={[
          {
            title: t("header.orderId"),
            render: (item) => (
              <Tooltip title={item.payment_id}>
                <div className="w-full px-2 py-0.5 flex items-center justify-center rounded-full border border-gray-v2-200 bg-gray-v2-50 text-xs font-medium text-gray-v2-700">
                  {item.payment_id.slice(-6)}
                </div>
              </Tooltip>
            ),
            gridTemplateValue: "2fr",
          },
          {
            title: t("header.productService"),
            render: (item) =>
              locale === "vi" ? item.product.name.vi : item.product.name.en,
            gridTemplateValue: "4fr",
          },
          {
            title: t("header.subscription"),
            render: (item) =>
              locale === "vi"
                ? item.product_plan.name.vi
                : item.product_plan.name.en,
            gridTemplateValue: "2fr",
          },
          {
            title: t("header.billingCycle"),
            render: (item) => {
              const findRemappedDuration = expandPlanDuration(item);
              return findRemappedDuration
                ? findRemappedDuration.getSpan()
                : item.duration;
            },
            gridTemplateValue: "2fr",
          },
          {
            title: t("header.totalPrice"),
            render: (item) =>
              item.sub_total.toLocaleString() + " " + item.currency,
            gridTemplateValue: "3fr",
          },
          {
            title: t("header.paymentDate"),
            render: (item) =>
              item.paid_time
                ? DateTime.fromSeconds(item.paid_time).toFormat("dd/MM/yyyy")
                : "-",
            gridTemplateValue: "2fr",
          },
          {
            title: t("header.paymentMethod"),
            render: (item) =>
              item.payment_method === PaymentMethodValue.BANKING ? (
                <CustomBadgeV2ForStatus
                  name={t("filter.paymentMethod.banking")}
                  icon={
                    <Repeat02 className="h-3 w-3 min-w-3 text-blue-light-v2" />
                  }
                  color={"blue-light"}
                />
              ) : item.payment_method === PaymentMethodValue.CARD ? (
                <CustomBadgeV2ForStatus
                  name={t("filter.paymentMethod.card")}
                  icon={
                    <CreditCard02 className="h-3 w-3 min-w-3 text-blue-light-v2" />
                  }
                  color={"blue-light"}
                  overwriteClassName="bg-white"
                />
              ) : (
                "-"
              ),
            gridTemplateValue: "2fr",
          },
          {
            title: t("header.status"),
            render: (item) =>
              item.status === PaymentStatusValue.PAID ? (
                <CustomBadgeV2ForStatus
                  name={t("filter.paymentStatus.paid")}
                  icon={
                    <CheckCircle className="h-3 w-3 min-w-3 text-success-v2" />
                  }
                  color={"success"}
                />
              ) : item.status === PaymentStatusValue.PROCESSING ? (
                <CustomBadgeV2ForStatus
                  name={t("filter.paymentStatus.processing")}
                  icon={<Clock className="h-3 w-3 min-w-3 text-warning-v2" />}
                  color={"warning"}
                />
              ) : (
                "-"
              ),
            gridTemplateValue: "3fr",
          },
        ]}
        options={{ fitPageHeight: true }}
        pagination={{
          count: orderCount,
          selected: selectedPage,
          onChange: onChangePage,
          size: { value: pageSize, onChange: onChangePageSize },
        }}
      />
    </>
  );
};
